import { Refine, Authenticated } from '@refinedev/core';
import { ThemedLayoutV2, ThemedSiderV2, useNotificationProvider } from '@refinedev/antd';
import routerBindings, {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from '@refinedev/react-router';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SafetyOutlined,
  LockOutlined,
  BankOutlined,
  LineChartOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  ExperimentOutlined,
  FlagOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';

import { sfTheme } from './config/theme';
import serveFirstLogo from './assets/servefirst-logo.png';
import { APP_TITLE, backOfficeDocumentTitle } from './utils/documentTitle';
import {
  authProvider,
  createDataProvider,
  apiClient,
  authClient,
  accessControlProvider,
} from './providers';
import { DashboardPage } from './pages/dashboard';
import {
  EvaOverviewPage,
  EvaCompanyDetailPage,
  EvaSuperAdminsPage,
  EvaUserDetailPage,
  EvaPlatformDefaultsPage,
} from './pages/eva';
import { PlatformFlagsPage } from './pages/platformFlags';
import {
  CompaniesListPage,
  CompanyDeletionsPage,
  CompanyDetailPage,
  CompanyEditPage,
} from './pages/companies';
import { UsersListPage, UserDetailPage } from './pages/users';
import { RolesListPage, RoleDetailPage, RolesCreatePage, RolesEditPage } from './pages/roles';
import {
  PermissionsListPage,
  PermissionDetailPage,
  PermissionsCreatePage,
} from './pages/permissions';
import {
  DemosListPage,
  DemoWizardPage,
  DemoDetailPage,
  DemoReviewPage,
  DemoProgressPage,
} from './pages/demos';
import { DEMOS_RESOURCE } from './pages/demos/constants';
import { COMPANIES_META } from './pages/companies/constants';
import { PrioritiesPage } from './pages/priorities';
import { PRIORITIES_RESOURCE } from './pages/priorities/constants';
import { EnvCopyPage, EnvCopyRunPage } from './pages/eng/envCopy';
import { MigrationsPage } from './pages/eng/migrations';
import { ENG_RESOURCES } from './pages/eng/menu';
import { LoginPage } from './pages/login';
import { AuthCallbackPage } from './pages/auth/callback';

import '@refinedev/antd/dist/reset.css';

const apiDataProvider = createDataProvider(apiClient);
const authDataProvider = createDataProvider(authClient);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConfigProvider theme={sfTheme}>
        <AntApp>
          <Refine
            routerProvider={routerBindings}
            authProvider={authProvider}
            dataProvider={{
              default: apiDataProvider,
              authDataProvider: authDataProvider,
            }}
            accessControlProvider={accessControlProvider}
            notificationProvider={useNotificationProvider}
            resources={[
              {
                name: 'dashboard',
                list: '/dashboard',
                meta: {
                  label: 'Dashboard',
                  icon: <DashboardOutlined />,
                },
              },
              {
                name: 'eva',
                list: '/eva',
                meta: {
                  label: 'Eva',
                  icon: <LineChartOutlined />,
                },
              },
              {
                // SF-3800: the `eva` parent gains child resources below
                // (eva-superadmins, SF-3736), which turns the parent menu item
                // into an expand/collapse group header and orphans the /eva
                // overview page from the sider. Add an explicit Overview child
                // so /eva stays reachable as the top entry under Eva.
                name: 'eva-overview',
                list: '/eva',
                meta: { parent: 'eva', label: 'Overview', icon: <LineChartOutlined /> },
              },
              {
                name: 'eva-superadmins',
                list: '/eva/super-admins',
                meta: {
                  // Nested under Eva in the sider. SF's internal Eva usage view,
                  // gated by the access-control provider on the Eva read
                  // PERMISSION `admin:eva:usage:read` (super-admins inherit via
                  // the admin:eva:* / * wildcards) — matching the page gate +
                  // sf-api requirePermissions, so the nav entry hides for users
                  // who lack it instead of showing a doomed 403 link (SF-3736).
                  parent: 'eva',
                  label: 'SF Internal',
                  icon: <SafetyCertificateOutlined />,
                },
              },
              {
                name: 'eva-platform-defaults',
                list: '/eva/platform-defaults',
                meta: {
                  // Nested under Eva in the sider; platform defaults affect
                  // every company (SF-3839). Gated by the access-control
                  // provider on the super-admin-only `admin:eva:platform:read`
                  // PERMISSION (super-admins inherit via the admin:eva:*
                  // wildcard) — matching the page gate + sf-api
                  // requirePermissions, so the nav entry hides for users who
                  // lack it instead of showing a doomed 403 link.
                  parent: 'eva',
                  label: 'Platform Defaults',
                  icon: <DollarOutlined />,
                },
              },
              {
                // SF-4762: global switches such as "Staff must sign in with
                // Google". Its own top-level menu item (not under Eva); the
                // access-control provider hides it without
                // admin:platform:flags:read, matching sf-api.
                name: 'platform-flags',
                list: '/platform-flags',
                meta: { label: 'Platform feature flags', icon: <FlagOutlined /> },
              },
              {
                name: 'companies',
                list: '/companies',
                show: '/companies/:id',
                edit: '/companies/:id/edit',
                meta: {
                  label: 'Companies',
                  icon: <BankOutlined />,
                  canDelete: false,
                  // apiPath is declared once in COMPANIES_META (constants.ts) and spread
                  // here so changing the backend path only requires a single edit.
                  ...COMPANIES_META,
                },
              },
              {
                // SF-4651: prospect demo wizard, gated on admin:demos:create.
                ...DEMOS_RESOURCE,
                meta: { label: 'Demos', icon: <ExperimentOutlined />, canDelete: false },
              },
              {
                // SF-4758: product priorities. No permission on the menu: who may rank is a team
                // on the sign-in account, which only sf-api knows, so the page asks GET /me.
                ...PRIORITIES_RESOURCE,
                meta: { label: 'Priorities', icon: <OrderedListOutlined /> },
              },
              // Engineering: Copy environment and Migrations (src/pages/eng/menu.tsx).
              ...ENG_RESOURCES,
              {
                name: 'users',
                list: '/users',
                show: '/users/:id',
                meta: {
                  label: 'Users',
                  icon: <UserOutlined />,
                  dataProviderName: 'authDataProvider',
                },
              },
              {
                name: 'roles',
                list: '/roles',
                show: '/roles/:id',
                create: '/roles/create',
                edit: '/roles/:id/edit',
                meta: {
                  label: 'Roles',
                  icon: <SafetyOutlined />,
                  dataProviderName: 'authDataProvider',
                },
              },
              {
                name: 'permissions',
                list: '/permissions',
                show: '/permissions/:key',
                create: '/permissions/create',
                meta: {
                  label: 'Permissions',
                  icon: <LockOutlined />,
                  dataProviderName: 'authDataProvider',
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    key="authenticated-inner"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <ThemedLayoutV2
                      Sider={() => (
                        <ThemedSiderV2
                          Title={({ collapsed }) => (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '12px 16px',
                                color: '#6E247F',
                                fontSize: 16,
                                fontWeight: 600,
                              }}
                            >
                              <img
                                src={serveFirstLogo}
                                alt="ServeFirst"
                                style={{ width: 36, height: 36, flexShrink: 0 }}
                              />
                              {!collapsed && <span>{APP_TITLE}</span>}
                            </div>
                          )}
                        />
                      )}
                    >
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route index element={<NavigateToResource resource="dashboard" />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/eva" element={<EvaOverviewPage />} />
                <Route path="/eva/companies/:companyId" element={<EvaCompanyDetailPage />} />
                <Route path="/eva/super-admins" element={<EvaSuperAdminsPage />} />
                <Route path="/eva/platform-defaults" element={<EvaPlatformDefaultsPage />} />
                <Route path="/eva/users" element={<EvaUserDetailPage />} />
                <Route path="/platform-flags" element={<PlatformFlagsPage />} />
                <Route path="/companies" element={<CompaniesListPage />} />
                {/* SF-4813: before /companies/:id so "deletions" is never read as an id. */}
                <Route path="/companies/deletions" element={<CompanyDeletionsPage />} />
                <Route path="/companies/:id" element={<CompanyDetailPage />} />
                <Route path="/companies/:id/edit" element={<CompanyEditPage />} />
                <Route path="/demos" element={<DemosListPage />} />
                <Route path="/demos/new" element={<DemoWizardPage />} />
                <Route path="/demos/:id" element={<DemoDetailPage />} />
                <Route path="/demos/:id/review" element={<DemoReviewPage />} />
                <Route path="/demos/:id/progress" element={<DemoProgressPage />} />
                <Route path="/priorities" element={<PrioritiesPage />} />
                <Route path="/eng/env-copy" element={<EnvCopyPage />} />
                <Route path="/eng/env-copy/runs/:id" element={<EnvCopyRunPage />} />
                <Route path="/eng/migrations" element={<MigrationsPage />} />
                <Route path="/users" element={<UsersListPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                <Route path="/roles" element={<RolesListPage />} />
                <Route path="/roles/create" element={<RolesCreatePage />} />
                <Route path="/roles/:id/edit" element={<RolesEditPage />} />
                <Route path="/roles/:id" element={<RoleDetailPage />} />
                <Route path="/permissions" element={<PermissionsListPage />} />
                <Route path="/permissions/create" element={<PermissionsCreatePage />} />
                <Route path="/permissions/:key" element={<PermissionDetailPage />} />
              </Route>
              <Route
                element={
                  <Authenticated key="authenticated-outer" fallback={<Outlet />}>
                    <NavigateToResource />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
              </Route>
            </Routes>
            <UnsavedChangesNotifier />
            <DocumentTitleHandler
              handler={({ autoGeneratedTitle }) => backOfficeDocumentTitle(autoGeneratedTitle)}
            />
          </Refine>
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
